import React, { Fragment, useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import {
  Typography, Grid, Paper, Button, CircularProgress, Divider, List, ListItem, ListItemText, Chip
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import * as Icons from '@material-ui/icons';
import { invokeApig } from '../libs/awsLib';

const useStyles = makeStyles((theme) => ({
  section: {
    margin: theme.spacing(3, 0),
    marginBottom: theme.spacing(10),
    width: 'inherit',
  },
  paper: {
    padding: theme.spacing(2),
    margin: '15px 0',
  },
  gridMaxMd: {
    [theme.breakpoints.up('md')]: {
      maxWidth: theme.breakpoints.values.lg,
    },
  },
  title: {
    fontSize: '22px',
    fontWeight: 600,
    padding: '15px',
  },
  userInfo: {
    padding: '15px',
  },
  detailItem: {
    margin: '10px 0',
  },
  sectionTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
    fontWeight: 500,
  },
  divider: {
    marginBottom: theme.spacing(2),
  },
  quotaUsage: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(2, 0),
  },
  quotaBar: {
    height: 10,
    borderRadius: 5,
    width: '100%',
    background: '#eee',
    position: 'relative',
    marginTop: theme.spacing(1),
  },
  quotaFill: {
    height: '100%',
    borderRadius: 5,
    position: 'absolute',
    left: 0,
    top: 0,
    background: '#16cd6f',
  },
  quotaText: {
    marginLeft: theme.spacing(2),
    fontSize: '0.9em',
    fontWeight: 'bold',
  },
  statusChip: {
    marginLeft: theme.spacing(1),
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  aliasContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    margin: theme.spacing(2, 0),
  }
}));

function UserDetailPage() {
  const classes = useStyles();
  const location = useLocation();
  const { organization, user } = location.state;
  const [isLoading, setIsLoading] = useState(true);
  const [userDetail, setUserDetail] = useState(null);
  const [isError, setIsError] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    getUserDetail();
  }, []);

  const getUserDetail = async () => {
    try {
      setIsLoading(true);
      const response = await invokeApig({
        path: "/workmail/user/detail",
        method: "POST",
        body: {
          organizationId: organization.OrganizationId,
          id: user.Id
        }
      });
      
      if (response.status === 'OK') {
        setUserDetail(response.user);
      } else {
        setMessage('Error al obtener los detalles del usuario');
        setIsError(true);
      }
    } catch (error) {
      setMessage('Error al obtener los detalles del usuario. Intenta nuevamente o consulta a Soporte.');
      setIsError(true);
    }
    setIsLoading(false);
  }

  const formatState = (state) => {
    switch (state) {
      case 'ENABLED':
        return <Chip label="Activo" color="primary" size="small" className={classes.statusChip} />;
      case 'DISABLED':
        return <Chip label="Deshabilitado" style={{ backgroundColor: '#ff9800', color: 'white' }} size="small" className={classes.statusChip} />;
      case 'DELETED':
        return <Chip label="Eliminado" style={{ backgroundColor: '#f44336', color: 'white' }} size="small" className={classes.statusChip} />;
      default:
        return <Chip label={state} size="small" className={classes.statusChip} />;
    }
  }

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  const renderQuotaUsage = (used, total) => {
    if (!used || !total) return 'Información no disponible';
    
    const usedValue = parseInt(used, 10) * 1024 * 1024;
    const totalValue = parseInt(total, 10) * 1024 * 1024;
    const percentage = Math.min(100, Math.round((usedValue / totalValue) * 100));
    
    let color = '#16cd6f'; // Verde por defecto
    if (percentage > 85) color = '#f44336'; // Rojo si está por encima del 85%
    else if (percentage > 70) color = '#ff9800'; // Naranja si está por encima del 70%
    
    return (
      <div>
        <div className={classes.quotaUsage}>
          <div className={classes.quotaBar}>
            <div className={classes.quotaFill} style={{ width: `${percentage}%`, backgroundColor: color }}></div>
          </div>
          <span className={classes.quotaText}>{percentage}%</span>
        </div>
        <Typography variant="body2">
          {formatBytes(usedValue)} utilizados de {formatBytes(totalValue)}
        </Typography>
      </div>
    );
  }

  return (
    <Grid container spacing={2} className={classes.section} justifyContent="center">
      <Grid item xs={12} sm={12} md={12} lg={12} className={classes.gridMaxMd}>
        <Paper className={classes.paper}>
          <Grid container className={classes.userInfo}>
            <Grid item sm={10}>
              <Typography gutterBottom>
                Dominio: <b>{organization.DefaultMailDomain}</b>
              </Typography>
              <Typography gutterBottom>
                Usuario: <b>{user.DisplayName}</b> - <b>{user.Email}</b>
              </Typography>
            </Grid>
            <Grid item sm={2}>
              <Button variant="contained" to='/users' component={RouterLink}>Volver a usuarios</Button>
            </Grid>
          </Grid>
        </Paper>
        
        <Paper className={classes.paper}>
          <Typography variant="h4" gutterBottom className={classes.title}>
            Detalle del usuario
          </Typography>
          
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
              <CircularProgress />
            </div>
          ) : isError ? (
            <Alert severity="error">{message}</Alert>
          ) : (
            <Fragment>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    <Icons.Person style={{ verticalAlign: 'middle', marginRight: 8 }} />
                    Información básica
                  </Typography>
                  <Divider className={classes.divider} />
                  <List>
                    <ListItem>
                      <ListItemText 
                        primary="Nombre completo" 
                        secondary={userDetail.DisplayName} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Email" 
                        secondary={userDetail.Email} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Nombre de usuario" 
                        secondary={userDetail.Name} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Nombre" 
                        secondary={userDetail.FirstName || 'No especificado'} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Apellido" 
                        secondary={userDetail.LastName || 'No especificado'} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary={
                          <span>
                            Estado
                            {userDetail.State && formatState(userDetail.State)}
                          </span>
                        } 
                        secondary={userDetail.State === 'ENABLED' ? 'Usuario activo y operativo' : userDetail.State === 'DISABLED' ? 'Usuario deshabilitado temporalmente' : 'Estado desconocido'} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Fecha de creación" 
                        secondary={userDetail.CreatedTimestamp ? userDetail.CreatedTimestamp : 'No disponible'} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Última modificación" 
                        secondary={userDetail.LastModifiedTimestamp ? userDetail.LastModifiedTimestamp : 'No disponible'} 
                      />
                    </ListItem>
                  </List>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    <Icons.Storage style={{ verticalAlign: 'middle', marginRight: 8 }} />
                    Cuota de almacenamiento
                  </Typography>
                  <Divider className={classes.divider} />
                  {userDetail.Quota ? (
                    renderQuotaUsage(userDetail.Quota.Used, userDetail.Quota.Total)
                  ) : (
                    <Typography variant="body1" style={{ margin: '10px 0' }}>
                      Información de cuota no disponible.
                    </Typography>
                  )}
                  
                  <Typography variant="h6" className={classes.sectionTitle}>
                    <Icons.AlternateEmail style={{ verticalAlign: 'middle', marginRight: 8 }} />
                    Alias
                  </Typography>
                  <Divider className={classes.divider} />
                  {userDetail.Aliases && userDetail.Aliases.length > 0 ? (
                    <div className={classes.aliasContainer}>
                      {userDetail.Aliases.map((alias, index) => (
                        <Chip
                          key={index}
                          label={alias}
                          className={classes.chip}
                          icon={<Icons.Mail style={{ fontSize: 20 }} />}
                        />
                      ))}
                    </div>
                  ) : (
                    <Typography variant="body1" style={{ margin: '10px 0' }}>
                      Este usuario no tiene alias configurados.
                    </Typography>
                  )}
                  
                  <Typography variant="h6" className={classes.sectionTitle}>
                    <Icons.Group style={{ verticalAlign: 'middle', marginRight: 8 }} />
                    Pertenencia a grupos
                  </Typography>
                  <Divider className={classes.divider} />
                  {userDetail.Groups && userDetail.Groups.length > 0 ? (
                    <div className={classes.aliasContainer}>
                      {userDetail.Groups.map((group, index) => (
                        <Chip
                          key={index}
                          label={group.Name}
                          className={classes.chip}
                          icon={<Icons.Group style={{ fontSize: 20 }} />}
                        />
                      ))}
                    </div>
                  ) : (
                    <Typography variant="body1" style={{ margin: '10px 0' }}>
                      Este usuario no pertenece a ningún grupo.
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Fragment>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
}

export default UserDetailPage;